/**
 * useBackgroundJobDetails Hook
 *
 * Fetches details for a specific background job.
 */

import { useQuery } from '@tanstack/react-query';
import { backgroundJobService } from '../../../api/background-job.service';
import type { BackgroundJob } from '../../../types/background-job.types';
import { backgroundJobKeys } from './useBackgroundJobs';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook to fetch a specific background job's details
 */
export function useBackgroundJobDetails(
  tenantId: string,
  jobId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false | ((data: BackgroundJob | undefined) => number | false);
  }
) {
  const { enabled = true, refetchInterval = false } = options || {};

  return useQuery<BackgroundJob, Error>({
    queryKey: backgroundJobKeys.detail(tenantId, jobId),
    queryFn: async () => {
      try {
        return await backgroundJobService.getJob(tenantId, jobId);
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: enabled && !!tenantId && !!jobId,
    refetchInterval,
  });
}
